import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import {
  Card,
  Carousel,
  Space,
  Grid,
  Typography,
  Flex,
  Avatar,
  Image,
  Button,
  Spin,
  Row,
  Col,
  Badge,
} from "antd";
import {
  CustomerServiceOutlined,
  LoadingOutlined,
  MailOutlined,
  MoneyCollectOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";

import publicDataServices from "../../../APIs/publicDataServices";

import homePageBackground from "../../../Images/Common/home-page-background.png";
import BestSellerCarousel from "../../../Components/Common/bestSellerCarousel";
import {
  NextArrow,
  PrevArrow,
  BannerNextArrow,
  BannerPrevArrow,
} from "../../../Components/Common/reactSlickCustomArrows";
import { toSlug } from "../../../Components/Common/helpers";

import reno from "../../../Images/Logos/reno.png";
import bramah from "../../../Images/Logos/bramah.png";
import dgtango from "../../../Images/Logos/dgtango.png";
import hauss from "../../../Images/Logos/hauss.png";
import vtech from "../../../Images/Logos/vtech.png";
import dtc from "../../../Images/Logos/dtc.png";
import blum from "../../../Images/Logos/blum.png";
import cyberlock from "../../../Images/Logos/cyberlock.png";
import makita from "../../../Images/Logos/makita.png";
import lamp from "../../../Images/Logos/lamp.png";
import stguchi from "../../../Images/Logos/stguchi.png";
import maica from "../../../Images/Logos/maica.png";
import lamitak from "../../../Images/Logos/lamitak.png";
import virgo from "../../../Images/Logos/virgo.png";
import croma from "../../../Images/Logos/croma.png";
import team from "../../../Images/Logos/team.png";

import { settings } from "../../../Services/apiSetting";

const { Title, Text } = Typography;

const { useBreakpoint } = Grid;

const brands = [
  { name: "Reno", logo: reno },
  { name: "Bramah", logo: bramah },
  { name: "DGTango", logo: dgtango },
  { name: "Hauss", logo: hauss },
  { name: "VTech", logo: vtech },
  { name: "DTC", logo: dtc },
  { name: "Blum", logo: blum },
  { name: "CyberLock", logo: cyberlock },
  { name: "Makita", logo: makita },
  { name: "Lamp", logo: lamp },
  { name: "St. Guchi", logo: stguchi },
  { name: "Maica", logo: maica },
  { name: "Lamitak", logo: lamitak },
  { name: "Virgo", logo: virgo },
  { name: "Croma", logo: croma },
  { name: "Team", logo: team },
];

const Home = () => {
  const screens = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const [promotions, setPromotions] = useState([]);
  const [featuredCategories, setFeaturedCategories] = useState([]);

  const [bestSellerCategory, setBestSellerCategory] = useState(null);

  const [carouselImages, setCarouselImages] = useState([]);

  const [carouselLoaded, setCarouselLoaded] = useState(false);
  const isMobile = !screens.md; // true if screen width is less than 768px

  // load carousel images on screen size change
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const jsonName = isMobile
          ? "homepageCarouselMobile.json"
          : "homepageCarousel.json";

        const url = settings.homePageCarouselImageFolder + jsonName;

        const res = await fetch(url);

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();

        const mappedData = data.map((item) => ({
          url: settings.homePageCarouselImageFolder + item.fileName,
          alt: item.alt || "",
        }));

        console.log("Loaded homepage carousel images:", mappedData);

        setCarouselImages(mappedData);
      } catch (err) {
        console.error("Failed to load homepage carousel:", err);
      } finally {
        setCarouselLoaded(true);
        setIsLoading(false); // hide loading spinner after loading
      }
    };

    setCarouselLoaded(false); // reset before loading new images
    setIsLoading(true); // show loading spinner while fetching

    loadCarousel();
  }, [isMobile]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const [promoRes, categoryRes] = await Promise.all([
          getPromotions(),
          getFeaturedCategory(),
          // loadCarousel(),
        ]);

        // ⬇ After both API calls finish, handle scroll
        if (location?.state?.scrollTo === "promotion-section") {
          setTimeout(() => {
            const element = document.getElementById("promotion-section");

            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
          }, 800);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const getPromotions = async () => {
      try {
        const response = await publicDataServices.getPromotions();

        const mappedData = JSON.parse(response.data.data).map((item) => ({
          ...item,
          FirstImageTagName:
            settings.promoImageFolder + item.FirstImageTagName.trim(),
        }));

        setPromotions(mappedData);
      } catch (error) {
        console.error("Error fetching promotions:", error);
      }
    };

    const getFeaturedCategory = async () => {
      try {
        const response = await publicDataServices.getFeaturedCategory();

        const MAX_ITEMS = 8;

        const mappedData = JSON.parse(response.data.data).map((item) => ({
          ...item,
          Id: item.CategoryId,
          FirstImageTagName:
            settings.catalogsImageFolder + item.ImageTag?.trim(),
          Products: item.Products.slice(0, MAX_ITEMS),
        }));

        // console.log("Featured Categories:", mappedData);

        // map "Best Seller" category using FeaturedOrder of 1
        const bestSeller = mappedData.find((cat) => cat.FeaturedOrder === 1);

        setBestSellerCategory(bestSeller || null);

        // remove "Best Seller" from featured categories
        const filteredCategories = mappedData.filter(
          (cat) => cat.FeaturedOrder !== 1,
        );

        setFeaturedCategories(filteredCategories);
      } catch (error) {
        console.error("Error fetching featured categories:", error);
      }
    };

    // const loadCarousel = async () => {
    //   try {
    //     const jsonName = isMobile
    //       ? "homepageCarouselMobile.json"
    //       : "homepageCarousel.json";

    //     const url = settings.homePageCarouselImageFolder + jsonName;

    //     const res = await fetch(url);

    //     if (!res.ok) {
    //       throw new Error(`HTTP ${res.status}`);
    //     }

    //     const data = await res.json();

    //     const mappedData = data.map((item) => ({
    //       url: settings.homePageCarouselImageFolder + item.fileName,
    //       alt: item.alt || "",
    //     }));

    //     console.log("Loaded homepage carousel images:", mappedData);

    //     setCarouselImages(mappedData);
    //   } catch (err) {
    //     console.error("Failed to load homepage carousel:", err);
    //   }
    // };

    fetchData();
  }, []);

  const bannerSliderSettings = {
    autoplay: true,
    infinite: true,
    arrows: true,
    prevArrow: <BannerPrevArrow />,
    nextArrow: <BannerNextArrow />,
    dots: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    className: "no-margin-box-carousel home-banner-carousel",
  };

  //#region Prefetching logic
  function prefetchImages(urls) {
    urls.forEach((url) => {
      const img = new window.Image();
      img.src = url;
    });
  }

  function useInView({ rootMargin = "800px" } = {}) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
      if (!ref.current || inView) return;

      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        },
        { rootMargin },
      );

      obs.observe(ref.current);
      return () => obs.disconnect();
    }, [inView, rootMargin]);

    return { ref, inView };
  }

  //#endregion Prefetching logic

  const CategorySection = React.memo(function CategorySection({
    category,
    index,
    screens,
    settings,
    navigate,
    nextCategory,
  }) {
    const { ref, inView } = useInView({ rootMargin: "800px" });

    useEffect(() => {
      if (!inView || !nextCategory) return;

      const urls = nextCategory.Products.slice(0, 6).map(
        (p) => settings.productImageFolder + p.CoverImage,
      );

      const safePrefetch = () => {
        try {
          prefetchImages(urls);
        } catch (e) {
          console.error("prefetchImages crashed:", e);
        }
      };

      const ric = window.requestIdleCallback;

      if (typeof ric === "function") ric(safePrefetch);
      else setTimeout(safePrefetch, 200);
    }, [inView, nextCategory, settings.productImageFolder]);

    const isMobileProductList = screens.xs;
    const desired = isMobileProductList ? 2.35 : screens.md ? 2 : 1;
    const total = category.Products.length;
    const slidesToShow = Math.min(desired, total);
    const canSlide = total > slidesToShow;

    const sliderSettings = {
      variableWidth: !isMobileProductList,
      slidesToShow,
      slidesToScroll: 1,
      infinite: !isMobileProductList && canSlide,
      autoplay: false,
      arrows: canSlide,
      dots: false,
      swipeToSlide: true,
      draggable: true,
      accessibility: false,
      prevArrow: <PrevArrow />,
      nextArrow: <NextArrow />,
    };

    const eagerCount = slidesToShow + 2;

    return (
      <div ref={ref} style={{ marginTop: index === 0 ? 0 : 30 }}>
        <p
          className="category-title"
          style={{ fontSize: screens.xs ? 20 : 30 }}
        >
          {category.CategoryName}
        </p>

        <Text
          className="view-more-title"
          style={{
            fontSize: screens.xs ? 10 : 14,
          }}
          onClick={() =>
            navigate("/products", {
              state: { category: { Id: category.Id } },
            })
          }
        >
          View more &gt;
        </Text>

        {inView ? (
          <Slider {...sliderSettings} className="home-product-carousel">
            {category.Products.map((item, idx) => (
              <div key={item.ProductId} className="find-me">
                {(() => {
                  const categoryText = (item.SubCategoryNames ?? []).join(", ");

                  const card = (
                    <Card
                      styles={{
                        header: {
                          height: isMobileProductList ? 54 : 72,
                          minHeight: isMobileProductList ? 54 : 72,
                          paddingLeft: isMobileProductList ? 8 : 24,
                          paddingRight: isMobileProductList ? 8 : 24,
                        },
                        title: {
                          height: "100%",
                          paddingTop: isMobileProductList ? 8 : 12,
                          paddingBottom: isMobileProductList ? 8 : 12,
                          overflow: "hidden",
                          whiteSpace: "normal",
                          boxSizing: "border-box",
                        },
                        cover: {
                          paddingTop: isMobileProductList ? 6 : 8,
                        },
                        body: {
                          paddingTop: isMobileProductList ? 8 : 10,
                          paddingBottom: 0,
                          paddingLeft: isMobileProductList ? 8 : 24,
                          paddingRight: isMobileProductList ? 8 : 24,
                          width: "100%",
                          minWidth: isMobileProductList ? 0 : 225,
                          boxSizing: "border-box",
                        },
                        actions: {
                          paddingLeft: isMobileProductList ? 8 : 20,
                          paddingRight: isMobileProductList ? 8 : 20,
                          paddingTop: 0,
                          paddingBottom: 0,
                          margin: 0,
                          borderTop: "none",
                        },
                      }}
                      style={{
                        cursor: "initial",
                        width: isMobileProductList ? "100%" : 280,
                        marginTop: isMobileProductList ? 8 : 10,
                        overflow: "hidden",
                      }}
                      hoverable={false}
                      title={
                        <Typography.Paragraph
                          ellipsis={{ rows: 2, tooltip: categoryText }}
                          style={{
                            margin: 0,
                            fontSize: isMobileProductList ? 10 : 14,
                            lineHeight: isMobileProductList ? 1.3 : 1.4,
                            color: "#6B7280",
                          }}
                        >
                          {categoryText}
                        </Typography.Paragraph>
                      }
                      cover={
                        <img
                          alt={item.ProductName}
                          loading="lazy"
                          decoding="async"
                          style={{
                            borderRadius: 4,
                            objectFit: "contain",
                            width:
                              isMobileProductList || screens.md ? "100%" : 240,
                            height: isMobileProductList ? 105 : 200,
                          }}
                          src={settings.productImageFolder + item.CoverImage}
                        />
                      }
                      actions={[
                        <Button
                          type="primary"
                          block
                          style={{
                            marginTop: 0,
                            backgroundColor: "#002753",
                            color: "#fff",
                            height: isMobileProductList ? 30 : undefined,
                            paddingLeft: isMobileProductList ? 4 : undefined,
                            paddingRight: isMobileProductList ? 4 : undefined,
                            fontSize: isMobileProductList ? 11 : undefined,
                          }}
                          className="view-details-btn"
                          onClick={() => {
                            navigate(
                              `/product/${toSlug(item.ProductName)}/${toSlug(
                                item.Sku,
                              )}/${item.ProductId}`,
                            );
                          }}
                        >
                          View Details
                        </Button>,
                      ]}
                    >
                      <Typography.Paragraph
                        ellipsis={{
                          rows: isMobileProductList ? 3 : 2,
                          tooltip: item.ProductName,
                        }}
                        style={{
                          width: "100%",
                          minHeight: isMobileProductList ? 48.6 : 50.5,
                          margin: 0,
                          fontSize: isMobileProductList ? 12 : 16,
                          lineHeight: isMobileProductList ? 1.35 : undefined,
                          color: "#002753",
                          fontWeight: "bold",
                        }}
                      >
                        {item.ProductName}
                      </Typography.Paragraph>

                      <p
                        style={{
                          margin: 0,
                          marginTop: isMobileProductList ? 4 : 5,
                          fontSize: isMobileProductList ? 10 : 14,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#6B7280",
                        }}
                      >
                        {item.Sku}
                      </p>
                    </Card>
                  );

                  // ribbons logic (unchanged)
                  let wrapped = card;

                  if (item.Promotions && item.Promotions.length > 0) {
                    wrapped = (
                      <div style={{ position: "relative" }}>
                        <div
                          style={{
                            position: "absolute",
                            top: 10,
                            left: -12,
                            background: "#FFDC00",
                            color: "#fff",
                            padding: "2px 10px",
                            transform: "rotate(-45deg)",
                            fontWeight: "bold",
                            zIndex: 1,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                            borderRadius: 4,
                            color: "black",
                          }}
                        >
                          Promo
                        </div>
                        {wrapped}
                      </div>
                    );
                  }

                  if (item.RibbonText) {
                    wrapped = (
                      <Badge.Ribbon text={item.RibbonText} color="red">
                        {wrapped}
                      </Badge.Ribbon>
                    );
                  }

                  return wrapped;
                })()}
              </div>
            ))}
          </Slider>
        ) : (
          // Placeholder keeps layout stable
          <div
            style={{
              height: 360,
              borderRadius: 8,
              background: "#f5f5f5",
              padding: 20,
            }}
          />
        )}
      </div>
    );
  });

  return (
    <>
      {isLoading && (
        <Spin
          spinning={isLoading}
          indicator={<LoadingOutlined spin />}
          fullscreen
        />
      )}

      {!carouselLoaded ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <Spin spinning={true} indicator={<LoadingOutlined spin />} />
        </div>
      ) : (
        carouselImages.length !== 0 && (
          <Slider {...bannerSliderSettings}>
            {carouselImages.map((image, index) => (
              <img
                key={index}
                src={image.url}
                alt={image.alt || `Banner ${index + 1}`}
                className="home-banner-image"
              />
            ))}
          </Slider>
        )
      )}

      <BestSellerCarousel
        category={bestSellerCategory}
        screens={screens}
        settings={settings}
        toSlug={toSlug}
      />

      {/* Hero Image Section 
      <Space
        direction="vertical"
        align={screens.xs ? "center" : "center"}
        className="page-container"
        style={{
          justifyContent: "center",
          marginBottom: screens.xs ? 20 : 40,
        }}
      >
        <Flex
          vertical
          style={{
            marginTop: screens.xs ? 20 : 60,
            width: "100%",
            textAlign: "left",
            backgroundImage: `url(${homePageBackground})`,
            backgroundSize: "cover", // makes it fill nicely
            // backgroundPosition: "center",
            backgroundRepeat: "no-repeat", // prevent tiling
            textAlign: "center",
          }}
          wrap
          gap={screens.xs ? 15 : 25}
          justify="center"
          align="center"
          className="no-margin-box-carousel"
        >
          <p
            style={{
              width: "100%",
              fontSize: screens.xs ? 20 : 30,
              fontWeight: "bold",
              margin: 0,
              color: "#fff",
              marginTop: screens.xs ? 40 : 60,
            }}
          >
            Crafted for Perfection, Designed for You
          </p>

          <p
            style={{
              width: "80%",
              color: "#fff",
              fontSize: screens.xs ? 12 : 18,
              textAlign: "center",
              lineHeight: 1.3,
            }}
          >
            Enhance your home with premium furniture fittings and accessories
            built for style, durability, and functionality. From wardrobes to
            kitchens, Teamstar’s curated collection ensures every space is
            crafted with perfection and everyday convenience in mind.
          </p>

          <Button
            type="primary"
            style={{
              backgroundColor: "#ffffffff",
              color: "#fff",
              marginTop: screens.xs ? 10 : 20,
              marginBottom: screens.xs ? 30 : 60,
              color: "black",
              fontWeight: "bold",
              borderRadius: 25,
              padding: screens.xs ? 15 : 20,
            }}
            size={screens.xs ? "middle" : "large"}
            onClick={() => navigate("/contact-us")}
          >
            Get Consultation
          </Button>
        </Flex>
      </Space>
      */}

      {/* Categories */}
      <div
        style={{
          marginTop: 10,
          maxWidth: 2500,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {featuredCategories.map((category, index) => (
          <CategorySection
            key={category.Id}
            category={category}
            index={index}
            screens={screens}
            settings={settings}
            navigate={navigate}
            nextCategory={featuredCategories[index + 1]}
          />
        ))}

        {/* {featuredCategories.map((category, index) => {
          const desired = screens.md ? 2 : 1;

          const total = category.Products.length;

          const slidesToShow = Math.min(desired, total);

          const canSlide = total > slidesToShow;

          const sliderSettings = {
            variableWidth: !screens.xs,
            slidesToScroll: 1,
            infinite: canSlide,
            autoplay: canSlide,
            arrows: canSlide,
            dots: false,
            swipeToSlide: true,
            draggable: true,
            accessibility: false,
            prevArrow: <PrevArrow />,
            nextArrow: <NextArrow />,
          };

          return (
            <div
              key={index}
              style={{
                marginTop: index === 0 ? 0 : 30,
              }}
            >
              <p
                className="category-title"
                style={{
                  fontSize: screens.xs ? 20 : 30,
                }}
                onClick={() =>
                  navigate("/products", {
                    state: { category: { Id: category.Id } },
                  })
                }
              >
                {category.CategoryName}
              </p>

              <Slider {...sliderSettings}>
                {category.Products.map((item) => (
                  <div key={item.ProductId} className="find-me">
                    {(() => {
                      const card = (
                        <Card
                          styles={{
                            body: {
                              paddingTop: 10,
                              paddingBottom: 0,
                              width: "100%",
                              minWidth: 225,
                            },
                            actions: {
                              paddingLeft: 20,
                              paddingRight: 20,
                              paddingTop: 0,
                              paddingBottom: 0,
                              margin: 0,
                              borderTop: "none",
                            },
                          }}
                          style={{
                            cursor: "initial",
                            width: screens.xs ? "100%" : 280,
                            marginTop: 10,
                          }}
                          hoverable={false}
                          title={item.SubCategoryNames.map((sub, i) => (
                            <Text
                              key={i}
                              style={{
                                fontSize: screens.xs ? 12 : 14,
                                color: "#6B7280",
                                marginRight: 5,
                              }}
                            >
                              {sub}
                              {i < item.SubCategoryNames.length - 1 ? "," : ""}
                            </Text>
                          ))}
                          cover={
                            <Image
                              width={screens.xs || screens.md ? "100%" : 240}
                              height={200}
                              alt={item.ProductName}
                              style={{ borderRadius: 4, objectFit: "contain" }}
                              src={
                                settings.productImageFolder + item.CoverImage
                              }
                            />
                          }
                          actions={[
                            <Button
                              type="primary"
                              block
                              style={{
                                marginTop: 0,
                                backgroundColor: "#002753",
                                color: "#fff",
                              }}
                              className="view-details-btn"
                              onClick={() => {
                                navigate(
                                  `/product/${toSlug(
                                    item.ProductName,
                                  )}/${toSlug(item.Sku)}/${item.ProductId}`,
                                );
                              }}
                            >
                              View Details
                            </Button>,
                          ]}
                        >
                          <Typography.Paragraph
                            ellipsis={{
                              rows: 2,
                              tooltip: item.ProductName,
                            }}
                            style={{
                              width: "100%",
                              minHeight: 50.5,
                              margin: 0,
                              fontSize: 16,
                              color: "#002753",
                              fontWeight: "bold",
                            }}
                          >
                            {item.ProductName}
                          </Typography.Paragraph>

                          <p
                            style={{
                              margin: 0,
                              marginTop: 5,
                              fontSize: 14,
                              color: "#6B7280",
                            }}
                          >
                            {item.Sku}
                          </p>
                        </Card>
                      );

                      // ribbons logic (unchanged)
                      let wrapped = card;

                      if (item.Promotions && item.Promotions.length > 0) {
                        wrapped = (
                          <div style={{ position: "relative" }}>
                            <div
                              style={{
                                position: "absolute",
                                top: 10,
                                left: -12,
                                background: "#FFDC00",
                                color: "#fff",
                                padding: "2px 10px",
                                transform: "rotate(-45deg)",
                                fontWeight: "bold",
                                zIndex: 1,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                borderRadius: 4,
                                color: "black",
                              }}
                            >
                              Promo
                            </div>
                            {wrapped}
                          </div>
                        );
                      }

                      if (item.RibbonText) {
                        wrapped = (
                          <Badge.Ribbon text={item.RibbonText} color="red">
                            {wrapped}
                          </Badge.Ribbon>
                        );
                      }

                      return wrapped;
                    })()}
                  </div>
                ))}
              </Slider>
            </div>
          );
        })} */}
      </div>

      {/* Promotions */}
      <div
        style={{
          marginTop: 15,
          marginBottom: 40,
          maxWidth: 2500,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {promotions.length > 0 && (
          <Flex
            id="promotion-section"
            wrap
            gap={screens.xs ? 15 : 25}
            style={{
              marginTop: screens.xs ? 30 : 60,
              width: "100%",
            }}
          >
            <p
              style={{
                color: "#002753",
                fontSize: screens.xs ? 20 : 30,
                fontWeight: "bold",
                width: "fit-content",
                // textAlign: "left",
              }}
            >
              Our Promotions
            </p>

            <Row gutter={[16, 16]}>
              {promotions.map((promotion) => (
                <Col
                  key={promotion.Id}
                  xs={24}
                  sm={12}
                  md={12}
                  lg={8}
                  style={{
                    paddingLeft: 3,
                    paddingRight: 3,
                  }}
                >
                  <Image
                    src={promotion.FirstImageTagName}
                    alt={promotion.Name}
                    preview={false}
                    style={{ objectFit: "contain", cursor: "pointer" }}
                    onClick={() => {
                      navigate(
                        `/promotion-details/${promotion.Id}/${toSlug(
                          promotion.Name,
                        )}`,
                      );
                    }}
                  />
                </Col>
              ))}
            </Row>
          </Flex>
        )}
      </div>

      {/* 4 icons */}
      {/* <Space
        wrap="wrap"
        align="center"
        justify="center"
        style={{
          width: "100%",
          justifyContent: "center",
          marginTop: screens.xs ? 10 : 0,
          marginBottom: screens.xs ? 10 : 70,
        }}
      >
        <Card
          variant="borderless"
          style={{
            textAlign: "center",
            boxShadow: "none",
            background: "transparent",
          }}
        >
          <Space direction="vertical" align="center" size="middle">
            <Avatar
              size={70}
              style={{
                backgroundColor: "transparent",
                border: "3px solid #6a1b9a", // purple border
                color: "#6a1b9a",
                fontWeight: "bold",
              }}
            >
              <MailOutlined
                style={{
                  fontSize: 38,
                }}
              />
            </Avatar>

            <span>
              <Title
                level={5}
                style={{
                  margin: 0,
                  fontWeight: 400,
                  color: "#333",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Join</span> Our Telegram
                Group
              </Title>

              <Text type="secondary">Get The Latest Updates</Text>
            </span>
          </Space>
        </Card>

        <Card
          variant="borderless"
          style={{
            textAlign: "center",
            boxShadow: "none",
            background: "transparent",
          }}
        >
          <Space direction="vertical" align="center" size="middle">
            <Avatar
              size={70}
              style={{
                backgroundColor: "transparent",
                border: "3px solid #6a1b9a", // purple border
                color: "#6a1b9a",
                fontWeight: "bold",
              }}
            >
              <ShoppingOutlined
                style={{
                  fontSize: 38,
                }}
              />
            </Avatar>

            <span>
              <Title
                level={5}
                style={{ margin: 0, fontWeight: 400, color: "#333" }}
              >
                <span style={{ fontWeight: "bold" }}>Latest</span> Promotions
              </Title>

              <Text type="secondary">Hottest Deals And More!</Text>
            </span>
          </Space>
        </Card>

        <Card
          variant="borderless"
          style={{
            textAlign: "center",
            boxShadow: "none",
            background: "transparent",
          }}
        >
          <Space direction="vertical" align="center" size="middle">
            <Avatar
              size={70}
              style={{
                backgroundColor: "transparent",
                border: "3px solid #6a1b9a", // purple border
                color: "#6a1b9a",
                fontWeight: "bold",
              }}
            >
              <MoneyCollectOutlined
                style={{
                  fontSize: 38,
                }}
              />
            </Avatar>

            <span>
              <Title
                level={5}
                style={{
                  margin: 0,
                  fontWeight: 400,
                  color: "#333",
                }}
              >
                <span style={{ fontWeight: "bold" }}>Request</span> For Quote
                Instantly
              </Title>

              <Text type="secondary">Get Your Instant Quote Now!</Text>
            </span>
          </Space>
        </Card>

        <Card
          variant="borderless"
          style={{
            textAlign: "center",
            boxShadow: "none",
            background: "transparent",
          }}
        >
          <Space direction="vertical" align="center" size="middle">
            <Avatar
              size={70}
              style={{
                backgroundColor: "transparent",
                border: "3px solid #6a1b9a", // purple border
                color: "#6a1b9a",
                fontWeight: "bold",
              }}
            >
              <CustomerServiceOutlined
                style={{
                  fontSize: 38,
                }}
              />
            </Avatar>

            <span>
              <Title
                level={5}
                style={{ margin: 0, fontWeight: 400, color: "#333" }}
              >
                <span style={{ fontWeight: "bold" }}>Buying</span> Guide
              </Title>

              <Text type="secondary">Ask Our Experts</Text>
            </span>
          </Space>
        </Card>
      </Space> */}

      {/* brands */}
      <div
        style={{
          marginTop: screens.xs ? 20 : 80,
          marginBottom: 20,
          width: screens.xs ? "100%" : "90%",
          marginLeft: "auto",
          marginRight: "auto",
          position: "relative",
        }}
      >
        <p
          style={{
            color: "#002753",
            fontSize: screens.xs ? 20 : 30,
            fontWeight: "bold",
            width: "100%",
            textAlign: screens.xs ? "left" : "center",
            marginBottom: screens.xs ? -10 : 0,
            position: "absolute",
            zIndex: 2,
          }}
        >
          Brands We Carry
        </p>

        <Carousel
          autoplay
          infinite
          arrows={true}
          dots={false}
          slidesToShow={screens.xs ? 2 : 5}
          slidesToScroll={1}
          autoplaySpeed={3000}
          speed={500}
          pauseOnHover={false}
        >
          {brands.map((brand, i) => (
            <Flex key={i} justify="center" align="center">
              <Image
                src={brand.logo}
                alt={brand.name}
                preview={false}
                height={screens.xs ? 150 : 250}
                style={{
                  objectFit: "contain",
                  position: "relative",
                  zIndex: 1,
                }}
              />
            </Flex>
          ))}
        </Carousel>
      </div>
    </>
  );
};

export default Home;
